import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from 'environments/environment';
import { CameraDetail } from '@/common/interface/camera-detail.model';

export interface DashboardUpcomingTask {
    id: number;
    taskTitle: string;
    dueDateTime: string;
    assignedId: number;
    status: string;
    statusId: number;
}

// Response interface
export interface DashboardKeyMetrics {
    activeChicks: number;
    mortalityPercentage: number;
    feedingConsumption: string;
    plannedVsActualWeight: {
        planned: string;
        actual: string;
    };
    activeCycle: {
        name: string;
    };
    activeAlerts: number;
    sensorData: string;
    activeMedications: number;
    activeVaccinations: number;
}
export interface DashboardFarmHouseOverviewResponse {
    farmHouseName: string;

    liveSensors: {
        temperature: number;
        humidity: number;
        cO2: number;
        weight: number;
        waterFlow: number;
        lightIntensity: number;
    };

    weightProgress: {
        currentWeight: number;
        targetWeight: number;
    };

    batchSummary: {
        birdCount: number;
        birdAgeInDays: number;
        feedConsumptionRate: number;
        totalFeedConsumption: string;
    };

    environmentalSensors: {
        sensorType: string;
        sensors: {
            sensorLabel: string;
            sensorType: string;
            value: number;
            intervalWater: number;
            dailyWater: number;
            cycleWater: number;
        }[];
    }[];

    flowMonitoring: {
        waterFlow: number;
        weight: number;
        feedConsumptionRate: number;
    };

    cameraMonitoring: {
        cameraSnapshots: string[];
    };
}

export interface GetDashboardLiveAlertsRequest {
    farmId?: number;
    houseId?: number;
}
export interface AiInsight {
    suggestion: string;
    rationale: string;
    aiInsightActions: {
        title: string;
        priority: 'low' | 'medium' | 'high';
    }[];
}

export interface GetLiveAlertByNotificationResponse {
    notificationId: number;
    title: string;
    alertType: string | null;
    severity: string | null;
    value: number;
    timestamp: string;
    farmId: number;
    farmHouseId: number;
    farmHouseName: string;
    farmName: string;
    location: string;
    aiInsight: string | null;
    aiInsightResult: {
        suggestion: string;
        rationale: string;
        aiInsightActions: {
            title: string;
            priority: 'low' | 'medium' | 'high';
        }[];
    } | null;
}

export interface DashboardLiveAlert {
    alertType: string;
    value: number;
    timestamp: string;
    farmId?: number;
    farmHouseId?: number;
    farmHouseName?: string;
    farmName?: string;
    location?: string;
    aiInsight?: AiInsight;
}

export type GetDashboardLiveAlertsResponse = DashboardLiveAlert[];

export interface HouseWeight {
    avgWeight: number;
    isPredicted?: boolean;
}
export interface ChickGrowth {
    farmId: number;
    houseWeights: HouseWeight[];
}
export interface FlockHealthStatus {
    healthyChicks: number;
    mortalityChicks: number;
    mortalityRate: number;
}
export interface DashboardMainPageGraphs {
    chickGrowth: ChickGrowth[];
    flockHealthStatus: FlockHealthStatus;
}

export interface GetAIInsightsHouseResponse {
    finalWeightKg: number;
    insights: {
        title: string;
        severity: 'low' | 'medium' | 'high' | 'info';
        body: string;
        actions: string[];
    }[];
    forecast7dKg: number[];
    averageForecast7dKg: number;
    currentAverageWeight: number;
    weightProgress: number;
}

export interface EnvironmentalTimeSeriesDataPoint {
    timestamp: string;
    value: number;
}

export interface EnvironmentalTimeSeriesRequest {
    farmId: number;
    houseId: number;
    sensorType: 'Temperature' | 'Humidity' | 'CO2';
    startDate: string;
    endDate: string;
}

export interface EnvironmentalTimeSeriesResponse {
    sensorType: string;
    startDate: string;
    endDate: string;
    intervalMinutes: number;
    actual: EnvironmentalTimeSeriesDataPoint[];
    predicted: EnvironmentalTimeSeriesDataPoint[];
}

@Injectable({
    providedIn: 'root'
})
export class DashboardKeyMetricsService {
    private apiUrl = environment.apiUrl;
    constructor(private httpClient: HttpClient) { }

    getDashboardKeyMetrics(farmId: number): Observable<DashboardKeyMetrics> {
        return this.httpClient.get<DashboardKeyMetrics>(
            `${this.apiUrl}/DashboardKeyMetrics/GetDashboardKeyMetrics`,
            {
                params: { farmId: farmId }
            }
        );
    }

    getDashboardFarmOverview(userId: number, farmId: number) {
        return this.httpClient.get(
            `${this.apiUrl}/DashboardKeyMetrics/GetDashboardFarmOverview?userId=${userId}&farmId=${farmId}`
        );
    }

    getDashboardFarmHouseOverview(userId: number, houseId: number) {
        return this.httpClient.get<DashboardFarmHouseOverviewResponse>(
            `${this.apiUrl}/DashboardKeyMetrics/GetDashboardFarmHouseOverview?userId=${userId}&houseId=${houseId}`
        );
    }
    getDashboardLiveAlerts(
        params?: GetDashboardLiveAlertsRequest
    ): Observable<GetDashboardLiveAlertsResponse> {
        let httpParams = new HttpParams();
        if (params?.farmId !== undefined) {
            httpParams = httpParams.set('farmId', params.farmId.toString());
        }
        if (params?.houseId !== undefined) {
            httpParams = httpParams.set('houseId', params.houseId.toString());
        }

        return this.httpClient.get<GetDashboardLiveAlertsResponse>(
            `${this.apiUrl}/DashboardKeyMetrics/GetDashboardLiveAlerts`,
            { params: httpParams }
        );
    }

    getDashboardMainPageGraphs(
        farmId: number,
        cycleId: number
    ): Observable<DashboardMainPageGraphs> {
        return this.httpClient.get<DashboardMainPageGraphs>(
            `${this.apiUrl}/DashboardKeyMetrics/GetDashboardMainPageGraphs`,
            {
                params: { farmId: farmId, cycleId: cycleId, }
            }
        );
    }

    getDashboardUpcomingTasks(): Observable<DashboardUpcomingTask[]> {
        return this.httpClient
            .get<
                DashboardUpcomingTask[]
            >(`${this.apiUrl}/DashboardKeyMetrics/GetDashboardUpcomingTasks`)
            .pipe();
    }

    resolveAlert(notificationId: number) {
        return this.httpClient.post(
            `${this.apiUrl}/DashboardKeyMetrics/ResolveAlert?notificationId=${notificationId}`,
            {}
        );
    }

    getAIInsightsHouseResponse(
        cycleId: number
    ): Observable<GetAIInsightsHouseResponse> {
        return this.httpClient.get<GetAIInsightsHouseResponse>(
            `${this.apiUrl}/DashboardKeyMetrics/GetAIInsightsHouse?cycleId=${cycleId}`
        );
    }

    getLiveAlertByNotification(
        notificationId: number
    ): Observable<GetLiveAlertByNotificationResponse> {
        return this.httpClient.get<GetLiveAlertByNotificationResponse>(
            `${this.apiUrl}/DashboardKeyMetrics/GetLiveAlertByNotification?notificationId=${notificationId}`
        );
    }

    getCameraDetailsOfFarmhouse(farmhouseId: number) {
        return this.httpClient.get<CameraDetail[]>(
            `${this.apiUrl}/DashboardKeyMetrics/GetCamaradetailsOfFarmehouse?farmhouseId=${farmhouseId}`
        );
    }

    getEnvironmentalTimeSeries(
        request: EnvironmentalTimeSeriesRequest
    ): Observable<EnvironmentalTimeSeriesResponse> {
        let params = new HttpParams()
            .set('farmId', request.farmId.toString())
            .set('houseId', request.houseId.toString())
            .set('sensorType', request.sensorType)
            .set('startDate', request.startDate)
            .set('endDate', request.endDate);

        return this.httpClient.get<EnvironmentalTimeSeriesResponse>(
            `${this.apiUrl}/DashboardKeyMetrics/GetEnvironmentalTimeSeries`,
            { params }
        );
    }
}
